import { Helmet } from 'react-helmet-async';
import { filter } from 'lodash';
import { useState, useEffect } from 'react';
import axios from 'axios';
// @mui
import {
  Card,
  Table,
  Stack,
  Paper,
  Checkbox,
  TableRow,
  MenuItem,
  TableBody,
  TableCell,
  Container,
  Typography,
  TableContainer,
  TablePagination,
  Box,
  CircularProgress,
  TextField,
  Button,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
// components
import Iconify from '../../components/iconify';
import Scrollbar from '../../components/scrollbar';
// sections
import { ChildProfileListHead, ChildProfileListToolbar } from '../../sections/@dashboard/childprofile';
// mock
import { API_URL } from '../../api';
import { DeleteModal } from './DeleteModal';
import HowItWorksCard from '../../components/HowItWorksCard';
// ----------------------------------------------------------------------

const TABLE_HEAD = [
  { id: 'name', label: 'Name', alignRight: false },
  { id: 'email', label: 'Email', alignRight: false },
  { id: 'childName', label: 'Child Name', alignRight: false },
  { id: 'currentSchool', label: 'Current School', alignRight: false },
  { id: 'yearGroup', label: 'Year Group', alignRight: false },
  { id: 'reference', label: 'Reference', alignRight: false },

  { id: '', label: 'Actions', alignRight: true },
];

// ----------------------------------------------------------------------

function descendingComparator(a, b, orderBy) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

function getComparator(order, orderBy) {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function applySortFilter(array, comparator, query) {
  const stabilizedThis = array?.map((el, index) => [el, index]);
  stabilizedThis?.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  if (query) {
    return filter(array, (_childprofile) => _childprofile?.name?.toLowerCase()?.indexOf(query?.toLowerCase()) !== -1);
  }
  return stabilizedThis?.map((el) => el[0]);
}

export default function ChildProfilePage() {
  const [page, setPage] = useState(0);
  const [order, setOrder] = useState('asc');
  const [selected, setSelected] = useState([]);
  const [orderBy, setOrderBy] = useState('name');
  const [filterName, setFilterName] = useState('');
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [loading, setLoading] = useState(true);
  const [child, setChild] = useState();
  const [plus, setPlus] = useState(0);
  const [levelFilter, setLevelFilter] = useState('');
  const [subjectFilter, setSubjectFilter] = useState('');
  const [subjectOptions, setSubjectOptions] = useState([]);

  const navigate = useNavigate();

  const [deleteModal, setDeleteModal] = useState(false);
  const [deleteId, setDeleteId] = useState();

  const handleDeleteModal = (id) => {
    deleteId ? setDeleteId() : setDeleteId(id);
    setDeleteModal(!deleteModal);
  };

  // const deleteChild = (id) => {
  //   axios.delete(`${API_URL}/api/child/${id}`).then(
  //     () => setPlus(plus + 1),
  //     axios
  //       .get(`${API_URL}/api/child/`)
  //       .then((res) => setChild(res.data))
  //       .catch((error) => console.log(error))
  //   );
  // };

  useEffect(() => {
    axios
      .get(`${API_URL}/api/subjects`)
      .then((res) => setSubjectOptions(res.data || []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    const params = new URLSearchParams();
    if (levelFilter) params.set('level', levelFilter);
    if (subjectFilter) params.set('subject', subjectFilter);
    axios
      .get(`${API_URL}/api/child/${params.toString() ? `?${params.toString()}` : ''}`)
      .then((res) => {
        setChild(res.data);
        setLoading(false);
      })
      .catch((error) => {
        console.log(error);
        setLoading(false);
      });
  }, [plus, levelFilter, subjectFilter]);

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      const newSelecteds = child?.map((n) => n._id);
      setSelected(newSelecteds);
      return;
    }
    setSelected([]);
  };

  const handleClick = (event, name) => {
    const selectedIndex = selected.indexOf(name);
    let newSelected = [];
    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, name);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(selected.slice(0, selectedIndex), selected.slice(selectedIndex + 1));
    }
    setSelected(newSelected);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setPage(0);
    setRowsPerPage(parseInt(event.target.value, 10));
  };

  const handleFilterByName = (event) => {
    setPage(0);
    setFilterName(event.target.value);
  };

  const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - child?.length) : 0;

  const filteredChildProfiles = applySortFilter(child, getComparator(order, orderBy), filterName);

  const isNotFound = !filteredChildProfiles?.length && !!filterName;

  function detailsPage(id) {
    navigate(`/childprofile/details/${id}`);
  }
  return (
    <>
      <Helmet>
        <title> Child Profile Form | English Teacher </title>
      </Helmet>

      <Container>
        <Stack className="stacky">
          <Typography variant="h4" gutterBottom>
            Child Profile Form
          </Typography>
        </Stack>
        <Box sx={{ mb: 3 }}>
          <HowItWorksCard>
                        <Typography variant="caption" component="div" color="text.secondary">
                            <strong>1.</strong> Legacy contact-form submissions from an older version of the public site.<br />
                            <strong>2.</strong> View-only — new enquiries no longer land here (they go to <em>Contact enquiries</em> instead).<br />
                            <strong>3.</strong> Kept alive so historical enquiries stay reachable.<br />
                        </Typography>
                    </HowItWorksCard>
        </Box>


        <Card sx={{ borderRadius: '12px' }}>
          <Box sx={{ display: 'flex', gap: 2, p: 2, flexWrap: 'wrap' }}>
            <TextField
              select
              size="small"
              label="Level"
              value={levelFilter}
              onChange={(e) => setLevelFilter(e.target.value)}
              sx={{ minWidth: 140 }}
            >
              <MenuItem value="">Any</MenuItem>
              {['KS2', 'KS3', 'GCSE', 'A-Level'].map((l) => (
                <MenuItem key={l} value={l}>{l}</MenuItem>
              ))}
            </TextField>
            <TextField
              select
              size="small"
              label="Subject"
              value={subjectFilter}
              onChange={(e) => setSubjectFilter(e.target.value)}
              sx={{ minWidth: 200 }}
            >
              <MenuItem value="">Any</MenuItem>
              {subjectOptions.map((s) => (
                <MenuItem key={s._id} value={s.name}>{s.name}</MenuItem>
              ))}
            </TextField>
            {(levelFilter || subjectFilter) && (
              <Button onClick={() => { setLevelFilter(''); setSubjectFilter(''); }}>Clear</Button>
            )}
          </Box>
          <ChildProfileListToolbar
            selected={selected}
            setChild={setChild}
            numSelected={selected.length}
            filterName={filterName}
            onFilterName={handleFilterByName}
          />

          <Scrollbar>
            <TableContainer sx={{ minWidth: 800 }}>
              <Table>
                <ChildProfileListHead
                  order={order}
                  orderBy={orderBy}
                  headLabel={TABLE_HEAD}
                  rowCount={child?.length}
                  numSelected={selected.length}
                  onRequestSort={handleRequestSort}
                  onSelectAllClick={handleSelectAllClick}
                />
                {loading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                    <CircularProgress />
                  </Box>
                ) : (
                  <TableBody>
                    {filteredChildProfiles?.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)?.map((row) => {
                      const { _id, parentName, email, childName, school, group, reference } = row;
                      const selectedChildProfile = selected?.indexOf(_id) !== -1;

                      return (
                        <TableRow hover key={_id} tabIndex={-1} role="checkbox" selected={selectedChildProfile}>
                          <TableCell padding="checkbox">
                            <Checkbox
                              checked={selectedChildProfile}
                              onChange={(event) => handleClick(event, _id)}
                              sx={{ mr: 4 }}
                            />
                          </TableCell>

                          <TableCell
                            align="left"
                            sx={{ cursor: 'pointer', fontFamily: 'Inter', fontWeight: '600' }}
                            onClick={() => detailsPage(_id)}
                          >
                            {parentName}
                          </TableCell>
                          <TableCell
                            align="left"
                            sx={{ cursor: 'pointer', fontFamily: 'Inter', fontWeight: '400' }}
                            onClick={() => detailsPage(_id)}
                          >
                            {email}
                          </TableCell>

                          <TableCell
                            align="left"
                            sx={{ cursor: 'pointer', fontFamily: 'Inter', fontWeight: '400' }}
                            onClick={() => detailsPage(_id)}
                          >
                            {childName}
                          </TableCell>
                          <TableCell
                            align="left"
                            sx={{ cursor: 'pointer', fontFamily: 'Inter', fontWeight: '400' }}
                            onClick={() => detailsPage(_id)}
                          >
                            {school}
                          </TableCell>

                          <TableCell
                            align="left"
                            sx={{ cursor: 'pointer', fontFamily: 'Inter', fontWeight: '400' }}
                            onClick={() => detailsPage(_id)}
                          >
                            {group}
                          </TableCell>
                          <TableCell
                            align="left"
                            sx={{ cursor: 'pointer', fontFamily: 'Inter', fontWeight: '400' }}
                            onClick={() => detailsPage(_id)}
                          >
                            {reference}
                          </TableCell>

                          <TableCell align="right">
                            <MenuItem onClick={() => handleDeleteModal(_id)} sx={{ color: 'error.main' }}>
                              <Iconify icon={'eva:trash-2-outline'} />
                            </MenuItem>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                    {emptyRows > 0 && (
                      <TableRow style={{ height: 53 * emptyRows }}>
                        <TableCell colSpan={6} />
                      </TableRow>
                    )}
                  </TableBody>
                )}
                {isNotFound && (
                  <TableBody>
                    <TableRow>
                      <TableCell align="center" colSpan={6} sx={{ py: 3 }}>
                        <Paper
                          sx={{
                            textAlign: 'center',
                          }}
                        >
                          <Typography variant="h6" paragraph sx={{ fontFamily: 'Inter' }}>
                            Not found
                          </Typography>

                          <Typography variant="body2" sx={{ fontFamily: 'Inter' }}>
                            No results found for &nbsp;
                            <strong>&quot;{filterName}&quot;</strong>.
                            <br /> Try checking for typos or using complete words.
                          </Typography>
                        </Paper>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                )}
              </Table>
            </TableContainer>
          </Scrollbar>

          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={child?.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Card>
        <DeleteModal
          deleteModal={deleteModal}
          handleDeleteModal={handleDeleteModal}
          deleteId={deleteId}
          setChild={setChild}
          setPlus={setPlus}
          plus={plus}
        />
      </Container>
    </>
  );
}